/**
=========================================================
* Series Settings Page
=========================================================
* Configure registration form settings for cricket series
*/

import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";

// @mui material components
import {
  Card,
  CardContent,
  Grid,
  Box,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Divider,
  Chip,
  TextField,
} from "@mui/material";

// Soft UI Dashboard React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

// Soft UI Dashboard React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Icons
import SaveIcon from "@mui/icons-material/Save";
import SettingsIcon from "@mui/icons-material/Settings";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoIcon from "@mui/icons-material/Info";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

// API service
import { seriesSettingsAPI } from "services/api";

function SeriesSettings() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Get series_id from URL params
  const seriesId = searchParams.get("s") || searchParams.get("series_id");

  const [settings, setSettings] = useState({
    show_cric_heroes_profile_link: 0,
    show_batting_style_etc: 0,
    show_jersey_no: 0,
    show_jersey_lower_size: 0,
    show_gpay_screenshot: 0,
    show_aadhar_pic: 0,
    show_comments: 0,
    show_previous_team_details: 0,
    show_district: 0,
    district_option_values: "",
    show_designation: 0,
    show_office_location: 0,
    show_job_type: 0,
    job_type_option_values: "",
    show_office_id_photo: 0,
    show_foreign_player_details: 0,
    enable_registration_start_date: 0,
    registration_start_date_time: "",
    enable_registration_closing_date: 0,
    registration_closing_date_time: "",
    close_registration_temp: 0,
    max_response_count: "",
    show_keeping_flag: 0,
    show_check_application_status: 0,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Load settings on component mount
  useEffect(() => {
    if (seriesId) {
      loadSettings();
    } else {
      setError("Series ID is required. Please provide series_id in URL parameter.");
      setLoading(false);
    }
  }, [seriesId]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await seriesSettingsAPI.getSettings(seriesId);

      if (response.data.status === 1) {
        const settingsData = response.data.settings;
        setSettings({
          show_cric_heroes_profile_link: parseInt(settingsData.show_cric_heroes_profile_link || 0),
          show_batting_style_etc: parseInt(settingsData.show_batting_style_etc || 0),
          show_jersey_no: parseInt(settingsData.show_jersey_no || 0),
          show_jersey_lower_size: parseInt(settingsData.show_jersey_lower_size || 0),
          show_gpay_screenshot: parseInt(settingsData.show_gpay_screenshot || 0),
          show_aadhar_pic: parseInt(settingsData.show_aadhar_pic || 0),
          show_comments: parseInt(settingsData.show_comments || 0),
          show_previous_team_details: parseInt(settingsData.show_previous_team_details || 0),
          show_district: parseInt(settingsData.show_district || 0),
          district_option_values: settingsData.district_option_values || "",
          show_designation: parseInt(settingsData.show_designation || 0),
          show_office_location: parseInt(settingsData.show_office_location || 0),
          show_job_type: parseInt(settingsData.show_job_type || 0),
          job_type_option_values: settingsData.job_type_option_values || "",
          show_office_id_photo: parseInt(settingsData.show_office_id_photo || 0),
          show_foreign_player_details: parseInt(settingsData.show_foreign_player_details || 0),
          enable_registration_start_date: parseInt(settingsData.enable_registration_start_date || 0),
          registration_start_date_time: settingsData.registration_start_date_time || "",
          enable_registration_closing_date: parseInt(settingsData.enable_registration_closing_date || 0),
          registration_closing_date_time: settingsData.registration_closing_date_time || "",
          close_registration_temp: parseInt(settingsData.close_registration_temp || 0),
          max_response_count: settingsData.max_response_count || "",
          show_keeping_flag: parseInt(settingsData.show_keeping_flag || 0),
          show_check_application_status: parseInt(settingsData.show_check_application_status || 0),
        });
      } else {
        setError(response.data.message || "Failed to load settings");
      }
    } catch (err) {
      console.error("Error loading settings:", err);
      setError(err.response?.data?.message || "Failed to load settings. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (field) => {
    setSettings(prev => ({
      ...prev,
      [field]: prev[field] === 1 ? 0 : 1
    }));
    setSuccess(false);
  };

  const handleInputChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
    setSuccess(false);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);

      const response = await seriesSettingsAPI.bulkUpdateSettings({
        series_id: seriesId,
        settings: settings
      });

      if (response.data.status === 1) {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 5000);
      } else {
        setError(response.data.message || "Failed to save settings");
      }
    } catch (err) {
      console.error("Error saving settings:", err);
      setError(err.response?.data?.message || "Failed to save settings. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox py={3}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <Box textAlign="center">
              <CircularProgress size={60} color="primary" />
              <SoftTypography variant="h6" color="text" mt={2}>
                Loading settings...
              </SoftTypography>
            </Box>
          </Box>
        </SoftBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              {/* Header */}
              <Card elevation={3}>
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <SettingsIcon sx={{ fontSize: 40, color: "primary.main" }} />
                      <Box>
                        <SoftTypography variant="h4" color="dark" fontWeight="bold">
                          Series Settings (സീരീസ് ക്രമീകരണങ്ങൾ)
                        </SoftTypography>
                        <SoftTypography variant="body2" color="text">
                          Configure registration form fields for this series
                        </SoftTypography>
                      </Box>
                    </Box>
                    <SoftButton
                      variant="outlined"
                      color="secondary"
                      startIcon={<ArrowBackIcon />}
                      onClick={handleBack}
                    >
                      Back
                    </SoftButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card elevation={3}>
                <CardContent sx={{ p: 4 }}>
                  {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                      {error}
                    </Alert>
                  )}

                  {success && (
                    <Alert 
                      severity="success" 
                      sx={{ mb: 3 }}
                      icon={<CheckCircleIcon />}
                    >
                      Settings saved successfully!
                    </Alert>
                  )}

                  {/* Info Box */}
                  <Alert severity="info" sx={{ mb: 4 }} icon={<InfoIcon />}>
                    <SoftTypography variant="body2" color="dark">
                      <strong>Note:</strong> Enabled fields will be shown in the public registration form. 
                      Fields marked as "Required" will be mandatory for registration, 
                      while "Optional" fields can be left empty.
                    </SoftTypography>
                  </Alert>

                  <Grid container spacing={3}>
                    {/* Cric Heroes Profile Link */}
                    <Grid item xs={12} md={6}>
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          p: 2,
                          border: settings.show_cric_heroes_profile_link === 1 ? '2px solid #4CAF50' : '1px solid #e0e0e0',
                          backgroundColor: settings.show_cric_heroes_profile_link === 1 ? '#f1f8f4' : 'transparent'
                        }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <SoftTypography variant="h6" color="dark" fontWeight="medium">
                              Cric Heroes Profile Link
                            </SoftTypography>
                            <SoftTypography variant="caption" color="text">
                              <Chip label="Optional" size="small" color="default" sx={{ mt: 0.5 }} />
                            </SoftTypography>
                            <SoftTypography variant="body2" color="text" mt={1}>
                              Player's Cric Heroes profile URL
                            </SoftTypography>
                          </Box>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={settings.show_cric_heroes_profile_link === 1}
                                onChange={() => handleToggle('show_cric_heroes_profile_link')}
                                color="success"
                              />
                            }
                            label=""
                          />
                        </Box>
                      </Card>
                    </Grid>

                    {/* Batting/Bowling Style */}
                    <Grid item xs={12} md={6}>
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          p: 2,
                          border: settings.show_batting_style_etc === 1 ? '2px solid #4CAF50' : '1px solid #e0e0e0',
                          backgroundColor: settings.show_batting_style_etc === 1 ? '#f1f8f4' : 'transparent'
                        }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <SoftTypography variant="h6" color="dark" fontWeight="medium">
                              Cricket Skills
                            </SoftTypography>
                            <SoftTypography variant="caption" color="text">
                              <Chip label="Required" size="small" color="error" sx={{ mt: 0.5 }} />
                            </SoftTypography>
                            <SoftTypography variant="body2" color="text" mt={1}>
                              Role, batting style, bowling style, keeping
                            </SoftTypography>
                          </Box>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={settings.show_batting_style_etc === 1}
                                onChange={() => handleToggle('show_batting_style_etc')}
                                color="success"
                              />
                            }
                            label=""
                          />
                        </Box>
                      </Card>
                    </Grid>

                    {/* Jersey Details */}
                    <Grid item xs={12} md={6}>
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          p: 2,
                          border: settings.show_jersey_no === 1 ? '2px solid #4CAF50' : '1px solid #e0e0e0',
                          backgroundColor: settings.show_jersey_no === 1 ? '#f1f8f4' : 'transparent'
                        }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <SoftTypography variant="h6" color="dark" fontWeight="medium">
                              Jersey Information
                            </SoftTypography>
                            <SoftTypography variant="caption" color="text">
                              <Chip label="Required" size="small" color="error" sx={{ mt: 0.5 }} />
                            </SoftTypography>
                            <SoftTypography variant="body2" color="text" mt={1}>
                              Jersey number, name, and size
                            </SoftTypography>
                          </Box>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={settings.show_jersey_no === 1}
                                onChange={() => handleToggle('show_jersey_no')}
                                color="success"
                              />
                            }
                            label=""
                          />
                        </Box>
                      </Card>
                    </Grid>

                    {/* Jersey lower size (Pant size) */}
                    <Grid item xs={12} md={6}>
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          p: 2,
                          border: settings.show_jersey_lower_size === 1 ? '2px solid #4CAF50' : '1px solid #e0e0e0',
                          backgroundColor: settings.show_jersey_lower_size === 1 ? '#f1f8f4' : 'transparent'
                        }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <SoftTypography variant="h6" color="dark" fontWeight="medium">
                              Jersey lower size (Pant size)
                            </SoftTypography>
                            <SoftTypography variant="caption" color="text">
                              <Chip label="Required" size="small" color="error" sx={{ mt: 0.5 }} />
                            </SoftTypography>
                            <SoftTypography variant="body2" color="text" mt={1}>
                              Pant size dropdown on registration form
                            </SoftTypography>
                          </Box>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={settings.show_jersey_lower_size === 1}
                                onChange={() => handleToggle('show_jersey_lower_size')}
                                color="success"
                              />
                            }
                            label=""
                          />
                        </Box>
                      </Card>
                    </Grid>

                    {/* GPay Screenshot */}
                    <Grid item xs={12} md={6}>
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          p: 2,
                          border: settings.show_gpay_screenshot === 1 ? '2px solid #4CAF50' : '1px solid #e0e0e0',
                          backgroundColor: settings.show_gpay_screenshot === 1 ? '#f1f8f4' : 'transparent'
                        }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <SoftTypography variant="h6" color="dark" fontWeight="medium">
                              GPay Screenshot
                            </SoftTypography>
                            <SoftTypography variant="caption" color="text">
                              <Chip label="Required" size="small" color="error" sx={{ mt: 0.5 }} />
                            </SoftTypography>
                            <SoftTypography variant="body2" color="text" mt={1}>
                              Payment proof screenshot
                            </SoftTypography>
                          </Box>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={settings.show_gpay_screenshot === 1}
                                onChange={() => handleToggle('show_gpay_screenshot')}
                                color="success"
                              />
                            }
                            label=""
                          />
                        </Box>
                      </Card>
                    </Grid>

                    {/* Aadhar Card Upload */}
                    <Grid item xs={12} md={6}>
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          p: 2,
                          border: settings.show_aadhar_pic === 1 ? '2px solid #4CAF50' : '1px solid #e0e0e0',
                          backgroundColor: settings.show_aadhar_pic === 1 ? '#f1f8f4' : 'transparent'
                        }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <SoftTypography variant="h6" color="dark" fontWeight="medium">
                              Aadhar Card Upload
                            </SoftTypography>
                            <SoftTypography variant="caption" color="text">
                              <Chip label="Required" size="small" color="error" sx={{ mt: 0.5 }} />
                            </SoftTypography>
                            <SoftTypography variant="body2" color="text" mt={1}>
                              Front and back side of Aadhar card
                            </SoftTypography>
                          </Box>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={settings.show_aadhar_pic === 1}
                                onChange={() => handleToggle('show_aadhar_pic')}
                                color="success"
                              />
                            }
                            label=""
                          />
                        </Box>
                      </Card>
                    </Grid>

                    {/* Comments Field */}
                    <Grid item xs={12} md={6}>
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          p: 2,
                          border: settings.show_comments === 1 ? '2px solid #4CAF50' : '1px solid #e0e0e0',
                          backgroundColor: settings.show_comments === 1 ? '#f1f8f4' : 'transparent'
                        }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <SoftTypography variant="h6" color="dark" fontWeight="medium">
                              Additional Comments
                            </SoftTypography>
                            <SoftTypography variant="caption" color="text">
                              <Chip label="Optional" size="small" color="default" sx={{ mt: 0.5 }} />
                            </SoftTypography>
                            <SoftTypography variant="body2" color="text" mt={1}>
                              General comments/notes field
                            </SoftTypography>
                          </Box>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={settings.show_comments === 1}
                                onChange={() => handleToggle('show_comments')}
                                color="success"
                              />
                            }
                            label=""
                          />
                        </Box>
                      </Card>
                    </Grid>

                    {/* Previous Team Details */}
                    <Grid item xs={12} md={6}>
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          p: 2,
                          border: settings.show_previous_team_details === 1 ? '2px solid #4CAF50' : '1px solid #e0e0e0',
                          backgroundColor: settings.show_previous_team_details === 1 ? '#f1f8f4' : 'transparent'
                        }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <SoftTypography variant="h6" color="dark" fontWeight="medium">
                              Previous Team Details
                            </SoftTypography>
                            <SoftTypography variant="caption" color="text">
                              <Chip label="Required" size="small" color="error" sx={{ mt: 0.5 }} />
                            </SoftTypography>
                            <SoftTypography variant="body2" color="text" mt={1}>
                              Player's previous team or club name
                            </SoftTypography>
                          </Box>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={settings.show_previous_team_details === 1}
                                onChange={() => handleToggle('show_previous_team_details')}
                                color="success"
                              />
                            }
                            label=""
                          />
                        </Box>
                      </Card>
                    </Grid>

                    {/* District Field */}
                    <Grid item xs={12}>
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          p: 3,
                          border: settings.show_district === 1 ? '2px solid #4CAF50' : '1px solid #e0e0e0',
                          backgroundColor: settings.show_district === 1 ? '#f1f8f4' : 'transparent'
                        }}
                      >
                        <Box>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Box>
                              <SoftTypography variant="h6" color="dark" fontWeight="medium">
                                District Field (ജില്ല ഫീൽഡ്)
                              </SoftTypography>
                              <SoftTypography variant="caption" color="text">
                                <Chip label="Required when enabled" size="small" color="error" sx={{ mt: 0.5 }} />
                              </SoftTypography>
                              <SoftTypography variant="body2" color="text" mt={1}>
                                Dropdown field for selecting district. Configure options below (comma-separated).
                              </SoftTypography>
                            </Box>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={settings.show_district === 1}
                                  onChange={() => handleToggle('show_district')}
                                  color="success"
                                />
                              }
                              label=""
                            />
                          </Box>
                          
                          {settings.show_district === 1 && (
                            <Box mt={2}>
                              <SoftTypography variant="caption" fontWeight="bold" color="dark" mb={1} display="block">
                                District Options (comma-separated):
                              </SoftTypography>
                              <TextField
                                fullWidth
                                multiline
                                rows={3}
                                value={settings.district_option_values}
                                onChange={(e) => handleInputChange('district_option_values', e.target.value)}
                                placeholder="e.g. Chennai,Coimbatore,Madurai,Salem,Trichy,Thanjavur"
                                variant="outlined"
                                helperText="Enter district names separated by commas. These will appear as options in the registration form."
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    backgroundColor: 'white',
                                  }
                                }}
                              />
                              {settings.district_option_values && (
                                <Box mt={2}>
                                  <SoftTypography variant="caption" color="text" fontWeight="bold">
                                    Preview ({settings.district_option_values.split(',').filter(d => d.trim()).length} options):
                                  </SoftTypography>
                                  <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                                    {settings.district_option_values.split(',').filter(d => d.trim()).map((district, index) => (
                                      <Chip
                                        key={index}
                                        label={district.trim()}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                      />
                                    ))}
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          )}
                        </Box>
                      </Card>
                    </Grid>

                    {/* Office/Job Information Section */}
                    <Grid item xs={12}>
                      <Divider sx={{ my: 3 }}>
                        <Chip label="Office/Job Information Fields" color="primary" />
                      </Divider>
                    </Grid>

                    {/* Designation Field */}
                    <Grid item xs={12} md={6}>
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          p: 2,
                          border: settings.show_designation === 1 ? '2px solid #4CAF50' : '1px solid #e0e0e0',
                          backgroundColor: settings.show_designation === 1 ? '#f1f8f4' : 'transparent'
                        }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <SoftTypography variant="h6" color="dark" fontWeight="medium">
                              Designation (സ്ഥാനം)
                            </SoftTypography>
                            <SoftTypography variant="caption" color="text">
                              <Chip label="Required when enabled" size="small" color="error" sx={{ mt: 0.5 }} />
                            </SoftTypography>
                            <SoftTypography variant="body2" color="text" mt={1}>
                              Participant's job designation or title
                            </SoftTypography>
                          </Box>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={settings.show_designation === 1}
                                onChange={() => handleToggle('show_designation')}
                                color="success"
                              />
                            }
                            label=""
                          />
                        </Box>
                      </Card>
                    </Grid>

                    {/* Office Location Field */}
                    <Grid item xs={12} md={6}>
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          p: 2,
                          border: settings.show_office_location === 1 ? '2px solid #4CAF50' : '1px solid #e0e0e0',
                          backgroundColor: settings.show_office_location === 1 ? '#f1f8f4' : 'transparent'
                        }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <SoftTypography variant="h6" color="dark" fontWeight="medium">
                              Office Location (ഓഫീസ് സ്ഥലം)
                            </SoftTypography>
                            <SoftTypography variant="caption" color="text">
                              <Chip label="Required when enabled" size="small" color="error" sx={{ mt: 0.5 }} />
                            </SoftTypography>
                            <SoftTypography variant="body2" color="text" mt={1}>
                              Participant's office or workplace location
                            </SoftTypography>
                          </Box>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={settings.show_office_location === 1}
                                onChange={() => handleToggle('show_office_location')}
                                color="success"
                              />
                            }
                            label=""
                          />
                        </Box>
                      </Card>
                    </Grid>

                    {/* Job Type Field */}
                    <Grid item xs={12}>
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          p: 3,
                          border: settings.show_job_type === 1 ? '2px solid #4CAF50' : '1px solid #e0e0e0',
                          backgroundColor: settings.show_job_type === 1 ? '#f1f8f4' : 'transparent'
                        }}
                      >
                        <Box>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Box>
                              <SoftTypography variant="h6" color="dark" fontWeight="medium">
                                Job Type (ജോലി തരം)
                              </SoftTypography>
                              <SoftTypography variant="caption" color="text">
                                <Chip label="Required when enabled" size="small" color="error" sx={{ mt: 0.5 }} />
                              </SoftTypography>
                              <SoftTypography variant="body2" color="text" mt={1}>
                                Dropdown field for selecting job type. Configure options below (comma-separated).
                              </SoftTypography>
                            </Box>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={settings.show_job_type === 1}
                                  onChange={() => handleToggle('show_job_type')}
                                  color="success"
                                />
                              }
                              label=""
                            />
                          </Box>
                          
                          {settings.show_job_type === 1 && (
                            <Box mt={2}>
                              <SoftTypography variant="caption" fontWeight="bold" color="dark" mb={1} display="block">
                                Job Type Options (comma-separated):
                              </SoftTypography>
                              <TextField
                                fullWidth
                                multiline
                                rows={3}
                                value={settings.job_type_option_values}
                                onChange={(e) => handleInputChange('job_type_option_values', e.target.value)}
                                placeholder="e.g. Full-time,Part-time,Contract,Freelance"
                                variant="outlined"
                                helperText="Enter job type options separated by commas. These will appear as options in the registration form."
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    backgroundColor: 'white',
                                  }
                                }}
                              />
                              {settings.job_type_option_values && (
                                <Box mt={2}>
                                  <SoftTypography variant="caption" color="text" fontWeight="bold">
                                    Preview ({settings.job_type_option_values.split(',').filter(j => j.trim()).length} options):
                                  </SoftTypography>
                                  <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                                    {settings.job_type_option_values.split(',').filter(j => j.trim()).map((jobType, index) => (
                                      <Chip
                                        key={index}
                                        label={jobType.trim()}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                      />
                                    ))}
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          )}
                        </Box>
                      </Card>
                    </Grid>

                    {/* Office ID Photo Upload */}
                    <Grid item xs={12} md={6}>
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          p: 2,
                          border: settings.show_office_id_photo === 1 ? '2px solid #4CAF50' : '1px solid #e0e0e0',
                          backgroundColor: settings.show_office_id_photo === 1 ? '#f1f8f4' : 'transparent'
                        }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <SoftTypography variant="h6" color="dark" fontWeight="medium">
                              Office ID Photo (ഓഫീസ് ഐഡി ഫോട്ടോ)
                            </SoftTypography>
                            <SoftTypography variant="caption" color="text">
                              <Chip label="Required when enabled" size="small" color="error" sx={{ mt: 0.5 }} />
                            </SoftTypography>
                            <SoftTypography variant="body2" color="text" mt={1}>
                              Upload office ID card photo for verification
                            </SoftTypography>
                          </Box>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={settings.show_office_id_photo === 1}
                                onChange={() => handleToggle('show_office_id_photo')}
                                color="success"
                              />
                            }
                            label=""
                          />
                        </Box>
                      </Card>
                    </Grid>

                    {/* Foreign Player Details */}
                    <Grid item xs={12} md={6}>
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          p: 2,
                          border: settings.show_foreign_player_details === 1 ? '2px solid #4CAF50' : '1px solid #e0e0e0',
                          backgroundColor: settings.show_foreign_player_details === 1 ? '#f1f8f4' : 'transparent'
                        }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <SoftTypography variant="h6" color="dark" fontWeight="medium">
                              Foreign Player (RO) Details
                            </SoftTypography>
                            <SoftTypography variant="caption" color="text">
                              <Chip label="Required when enabled" size="small" color="error" sx={{ mt: 0.5 }} />
                            </SoftTypography>
                            <SoftTypography variant="body2" color="text" mt={1}>
                              Text input for foreign player passport/visa details (max 100 characters)
                            </SoftTypography>
                          </Box>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={settings.show_foreign_player_details === 1}
                                onChange={() => handleToggle('show_foreign_player_details')}
                                color="success"
                              />
                            }
                            label=""
                          />
                        </Box>
                      </Card>
                    </Grid>

                    {/* Registration Timing Section */}
                    <Grid item xs={12}>
                      <Divider sx={{ my: 3 }}>
                        <Chip label="Registration Timing Controls" color="info" />
                      </Divider>
                    </Grid>

                    {/* Registration Start Time */}
                    <Grid item xs={12} md={6}>
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          p: 3,
                          border: settings.enable_registration_start_date === 1 ? '2px solid #2196F3' : '1px solid #e0e0e0',
                          backgroundColor: settings.enable_registration_start_date === 1 ? '#e3f2fd' : 'transparent'
                        }}
                      >
                        <Box>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Box>
                              <SoftTypography variant="h6" color="dark" fontWeight="medium">
                                Registration Start Date/Time
                              </SoftTypography>
                              <SoftTypography variant="caption" color="text">
                                <Chip label="Optional" size="small" color="default" sx={{ mt: 0.5 }} />
                              </SoftTypography>
                              <SoftTypography variant="body2" color="text" mt={1}>
                                Set when registration should open. Form will be blurred before this time.
                              </SoftTypography>
                            </Box>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={settings.enable_registration_start_date === 1}
                                  onChange={() => handleToggle('enable_registration_start_date')}
                                  color="info"
                                />
                              }
                              label=""
                            />
                          </Box>
                          
                          {settings.enable_registration_start_date === 1 && (
                            <Box mt={2}>
                              <TextField
                                fullWidth
                                type="datetime-local"
                                value={settings.registration_start_date_time}
                                onChange={(e) => handleInputChange('registration_start_date_time', e.target.value)}
                                variant="outlined"
                                helperText="Select the date and time when registration should start"
                                InputLabelProps={{
                                  shrink: true,
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    backgroundColor: 'white',
                                  }
                                }}
                              />
                            </Box>
                          )}
                        </Box>
                      </Card>
                    </Grid>

                    {/* Registration Closing Time */}
                    <Grid item xs={12} md={6}>
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          p: 3,
                          border: settings.enable_registration_closing_date === 1 ? '2px solid #2196F3' : '1px solid #e0e0e0',
                          backgroundColor: settings.enable_registration_closing_date === 1 ? '#e3f2fd' : 'transparent'
                        }}
                      >
                        <Box>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Box>
                              <SoftTypography variant="h6" color="dark" fontWeight="medium">
                                Registration Closing Date/Time
                              </SoftTypography>
                              <SoftTypography variant="caption" color="text">
                                <Chip label="Optional" size="small" color="default" sx={{ mt: 0.5 }} />
                              </SoftTypography>
                              <SoftTypography variant="body2" color="text" mt={1}>
                                Set when registration should close. Submit button will be disabled after this time.
                              </SoftTypography>
                            </Box>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={settings.enable_registration_closing_date === 1}
                                  onChange={() => handleToggle('enable_registration_closing_date')}
                                  color="info"
                                />
                              }
                              label=""
                            />
                          </Box>
                          
                          {settings.enable_registration_closing_date === 1 && (
                            <Box mt={2}>
                              <TextField
                                fullWidth
                                type="datetime-local"
                                value={settings.registration_closing_date_time}
                                onChange={(e) => handleInputChange('registration_closing_date_time', e.target.value)}
                                variant="outlined"
                                helperText="Select the date and time when registration should close"
                                InputLabelProps={{
                                  shrink: true,
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    backgroundColor: 'white',
                                  }
                                }}
                              />
                            </Box>
                          )}
                        </Box>
                      </Card>
                    </Grid>

                    {/* Temporary Close Registration */}
                    <Grid item xs={12} md={6}>
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          p: 3,
                          border: settings.close_registration_temp === 1 ? '2px solid #FF5722' : '1px solid #e0e0e0',
                          backgroundColor: settings.close_registration_temp === 1 ? '#ffebee' : 'transparent'
                        }}
                      >
                        <Box>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Box>
                              <SoftTypography variant="h6" color="dark" fontWeight="medium">
                                Temporarily Close Registration
                              </SoftTypography>
                              <SoftTypography variant="caption" color="text">
                                <Chip label="Emergency Stop" size="small" color="error" sx={{ mt: 0.5 }} />
                              </SoftTypography>
                              <SoftTypography variant="body2" color="text" mt={1}>
                                Enable this to immediately close registration regardless of other settings. 
                                You can re-open it anytime by disabling this option.
                              </SoftTypography>
                            </Box>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={settings.close_registration_temp === 1}
                                  onChange={() => handleToggle('close_registration_temp')}
                                  color="error"
                                />
                              }
                              label=""
                            />
                          </Box>
                          
                          {settings.close_registration_temp === 1 && (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                              <SoftTypography variant="caption" color="dark" fontWeight="bold">
                                ⚠️ Registration is currently CLOSED for all users
                              </SoftTypography>
                            </Alert>
                          )}
                        </Box>
                      </Card>
                    </Grid>

                    {/* Maximum Response Count */}
                    <Grid item xs={12} md={6}>
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          p: 3,
                          border: (settings.max_response_count && parseInt(settings.max_response_count) > 0) ? '2px solid #FF9800' : '1px solid #e0e0e0',
                          backgroundColor: (settings.max_response_count && parseInt(settings.max_response_count) > 0) ? '#fff3e0' : 'transparent'
                        }}
                      >
                        <Box>
                          <Box mb={2}>
                            <SoftTypography variant="h6" color="dark" fontWeight="medium">
                              Maximum Response Count
                            </SoftTypography>
                            <SoftTypography variant="caption" color="text">
                              <Chip label="Auto-close limit" size="small" color="warning" sx={{ mt: 0.5 }} />
                            </SoftTypography>
                            <SoftTypography variant="body2" color="text" mt={1}>
                              Registration will automatically close when this many responses are received. 
                              Leave empty or set to 0 for unlimited registrations.
                            </SoftTypography>
                          </Box>
                          
                          <Box mt={2}>
                            <TextField
                              fullWidth
                              type="number"
                              value={settings.max_response_count}
                              onChange={(e) => handleInputChange('max_response_count', e.target.value)}
                              placeholder="Leave empty or 0 for unlimited"
                              variant="outlined"
                              helperText="The remaining count is NOT shown to users; registration simply closes when limit is reached"
                              inputProps={{
                                min: 0,
                                step: 1
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: 'white',
                                }
                              }}
                            />
                          </Box>

                          {settings.max_response_count && parseInt(settings.max_response_count) > 0 && (
                            <Alert severity="info" sx={{ mt: 2 }}>
                              <SoftTypography variant="caption" color="dark">
                                📊 Registration will auto-close after <strong>{settings.max_response_count}</strong> responses
                              </SoftTypography>
                            </Alert>
                          )}
                        </Box>
                      </Card>
                    </Grid>

                    {/* Keeping Field */}
                    <Grid item xs={12} md={6}>
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          p: 2,
                          border: settings.show_keeping_flag === 1 ? '2px solid #4CAF50' : '1px solid #e0e0e0',
                          backgroundColor: settings.show_keeping_flag === 1 ? '#f1f8f4' : 'transparent'
                        }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <SoftTypography variant="h6" color="dark" fontWeight="medium">
                              Keeping Field (വിക്കറ്റ് കീപ്പിംഗ് ഫീൽഡ്)
                            </SoftTypography>
                            <SoftTypography variant="caption" color="text">
                              <Chip label="Optional" size="small" color="default" sx={{ mt: 0.5 }} />
                            </SoftTypography>
                            <SoftTypography variant="body2" color="text" mt={1}>
                              Show "Can Keep Wickets?" dropdown field in registration form
                            </SoftTypography>
                          </Box>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={settings.show_keeping_flag === 1}
                                onChange={() => handleToggle('show_keeping_flag')}
                                color="success"
                              />
                            }
                            label=""
                          />
                        </Box>
                      </Card>
                    </Grid>

                    {/* Check Application Status Field */}
                    <Grid item xs={12} md={6}>
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          p: 2,
                          border: settings.show_check_application_status === 1 ? '2px solid #4CAF50' : '1px solid #e0e0e0',
                          backgroundColor: settings.show_check_application_status === 1 ? '#f1f8f4' : 'transparent'
                        }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <SoftTypography variant="h6" color="dark" fontWeight="medium">
                              Check Application Status (അപേക്ഷ സ്റ്റാറ്റസ് പരിശോധിക്കുക)
                            </SoftTypography>
                            <SoftTypography variant="caption" color="text">
                              <Chip label="Optional" size="small" color="default" sx={{ mt: 0.5 }} />
                            </SoftTypography>
                            <SoftTypography variant="body2" color="text" mt={1}>
                              Allow users to check their registration status via OTP verification
                            </SoftTypography>
                          </Box>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={settings.show_check_application_status === 1}
                                onChange={() => handleToggle('show_check_application_status')}
                                color="success"
                              />
                            }
                            label=""
                          />
                        </Box>
                      </Card>
                    </Grid>
                  </Grid>

                  {/* Save Button */}
                  <Box mt={4} display="flex" justifyContent="center">
                    <SoftButton
                      variant="gradient"
                      color="primary"
                      size="large"
                      onClick={handleSave}
                      disabled={saving}
                      startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                      sx={{ minWidth: 200 }}
                    >
                      {saving ? "Saving..." : "Save Settings"}
                    </SoftButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </SoftBox>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default SeriesSettings;

